import { useCallback, useState } from 'react'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Events = Record<string, (...args: any[]) => void>

export type ArrayOfEvents<T extends Events> = {
  [K in keyof T]: T[K][]
}

export interface EventingHelpers<T extends Events> {
  addEventHandler<K extends keyof T>(eventType: K, callback: T[K]): void
  removeEventHandler<K extends keyof T>(eventType: K, callback: T[K]): void
  triggerEvent<K extends keyof T>(eventType: K, ...props: Parameters<T[K]>): void
}

export function useLocalEventing<T extends Events>(): EventingHelpers<T> {
  type TypedArrayOfEvents = ArrayOfEvents<T>
  const [events, setEvents] = useState<TypedArrayOfEvents>({} as TypedArrayOfEvents)

  type KeyInT = keyof T

  const addEventHandler = useCallback(
    <K extends KeyInT>(eventType: K, callback: T[K]): void => {
      setEvents(e => {
        const eventCallbacks = e[eventType] || []
        eventCallbacks.push(callback)

        e[eventType] = eventCallbacks
        return { ...e }
      })
    },
    [setEvents]
  )

  const removeEventHandler = useCallback(
    <K extends KeyInT>(eventType: K, callback: T[K]): void => {
      setEvents(e => {
        const eventCallbacks = e[eventType] || []
        const index = eventCallbacks.indexOf(callback)
        if (index > -1) {
          e[eventType].splice(index, 1)
        }
        return { ...e }
      })
    },
    [setEvents]
  )

  const triggerEvent = useCallback(
    async <K extends KeyInT>(eventType: K, ...props: Parameters<T[K]>): Promise<void> => {
      const eventCallbacks = events[eventType] || []
      await Promise.all(eventCallbacks.map(callback => callback(...props)))
    },
    [events]
  )

  return {
    addEventHandler,
    removeEventHandler,
    triggerEvent
  }
}
